import { LudopediaUser, LudopediaCollection, LudopediaGame } from '../../types';
import { getUserToken } from './ludopediaAuthService';
import { supabase } from '../../lib/supabase';

// Credenciais da aplicação
const LUDOPEDIA_CREDENTIALS = {
  app_id: '9827225de7dd9aff',
  app_key: 'ef79f58d2e0cb1c11a2fb93aae0d9054',
};

// URL base da API da Ludopedia (via proxy para evitar CORS)
const LUDOPEDIA_API_URL = '/api/ludopedia/api/v1';

/**
 * Obtém dados do usuário da Ludopedia
 * @param userId ID do usuário no sistema ou token direto
 * @returns Dados do usuário da Ludopedia ou null em caso de erro
 */
export async function getLudopediaUserData(userIdOrToken: string): Promise<LudopediaUser | null> {
  try {
    console.log('[Ludopedia] Buscando dados do usuário');
    
    // Determinar se o parâmetro é um userId ou um token
    let token = userIdOrToken;
    
    // Se não parece ser um token (tokens geralmente são longos), assumimos que é um userId
    if (userIdOrToken.length < 30) {
      token = await getUserToken(userIdOrToken) || '';
      
      if (!token) {
        console.error('[Ludopedia] Token não encontrado para o usuário:', userIdOrToken);
        return null;
      }
    }
    
    // Log de todos os cabeçalhos para depuração
    const headers = {
      'Content-Type': 'application/json',
      'APP-ID': LUDOPEDIA_CREDENTIALS.app_id,
      'APP-KEY': LUDOPEDIA_CREDENTIALS.app_key,
      'TOKEN': token
    };
    
    console.log('[Ludopedia] Enviando requisição de perfil com token:', token.substring(0, 10) + '...');
    
    // Obter dados do usuário da Ludopedia
    const response = await fetch(`${LUDOPEDIA_API_URL}/usuario`, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      console.error('[Ludopedia] Erro ao buscar dados do usuário:', response.status, response.statusText);
      return null;
    }
    
    const data = await response.json();
    
    if (!data || !data.usuario) {
      console.error('[Ludopedia] Dados do usuário em formato inválido:', data);
      return null;
    }
    
    // Converter para o formato esperado
    const user: LudopediaUser = {
      id: data.usuario.id || '',
      username: data.usuario.username || '',
      name: data.usuario.nome || data.usuario.username || '',
      avatar_url: data.usuario.avatar || null,
      access_token: token
    };
    
    console.log('[Ludopedia] Dados do usuário obtidos com sucesso:', user.username);
    return user;
  } catch (error) {
    console.error('[Ludopedia] Erro ao buscar dados do usuário:', error);
    return null;
  }
}

/**
 * Obtém a coleção de jogos do usuário da Ludopedia
 * @param userId ID do usuário no sistema ou token direto
 * @param page Número da página (inicia em 1)
 * @param perPage Jogos por página
 * @returns Coleção de jogos ou null em caso de erro
 */
export async function getLudopediaCollection(
  userIdOrToken: string,
  page: number = 1,
  perPage: number = 50
): Promise<LudopediaCollection | null> {
  try {
    console.log('[Ludopedia] Buscando coleção do usuário');
    
    // Determinar se o parâmetro é um userId ou um token
    let token = userIdOrToken;
    
    // Se não parece ser um token (tokens geralmente são longos), assumimos que é um userId
    if (userIdOrToken.length < 30) {
      token = await getUserToken(userIdOrToken) || '';
      
      if (!token) {
        console.error('[Ludopedia] Token não encontrado para o usuário:', userIdOrToken);
        return null;
      }
    }
    
    // Log dos headers enviados para depuração
    const headers = {
      'Content-Type': 'application/json',
      'APP-ID': LUDOPEDIA_CREDENTIALS.app_id,
      'APP-KEY': LUDOPEDIA_CREDENTIALS.app_key,
      'TOKEN': token
    };
    
    console.log('[Ludopedia] Enviando requisição para coleção com token:', token.substring(0, 10) + '...');
    
    // URL com os parâmetros de paginação
    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString()
    }).toString();
    
    // Obter coleção de jogos
    const response = await fetch(`${LUDOPEDIA_API_URL}/colecao?${queryParams}`, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      console.error('[Ludopedia] Erro ao buscar coleção:', response.status, response.statusText);
      return null;
    }
    
    const data = await response.json();
    
    // Verificar se os dados têm o formato esperado
    if (!data || !data.colecao || !Array.isArray(data.colecao.jogos)) {
      console.error('[Ludopedia] Dados da coleção em formato inválido:', data);
      return null;
    }
    
    // Formatar os jogos da coleção
    const games: LudopediaGame[] = data.colecao.jogos.map((jogo: any) => ({
      id: jogo.id_jogo?.toString() || jogo.id?.toString() || '',
      name: jogo.nm_jogo || jogo.nome || '',
      year: jogo.ano_publicacao || jogo.ano || 0,
      ano_nacional: jogo.ano_nacional || null,
      image_url: jogo.imagem || '',
      thumb_url: jogo.thumb || jogo.imagem_thumb || jogo.imagem || '',
      link: jogo.link || '',
      min_players: jogo.qt_jogadores_min || jogo.min_jogadores || 0,
      max_players: jogo.qt_jogadores_max || jogo.max_jogadores || 0,
      min_playing_time: jogo.vl_tempo_jogo || jogo.min_tempo || 0,
      max_playing_time: jogo.vl_tempo_jogo || jogo.max_tempo || jogo.min_tempo || 0,
      min_age: jogo.idade_minima || 0,
      categories: Array.isArray(jogo.categorias) ? jogo.categorias.map((cat: any) => cat.nome || cat) : [],
      mechanics: Array.isArray(jogo.mecanicas) ? jogo.mecanicas.map((mec: any) => mec.nome || mec) : [],
      themes: Array.isArray(jogo.temas) ? jogo.temas.map((tema: any) => tema.nome || tema) : [],
      artists: Array.isArray(jogo.artistas) ? jogo.artistas.map((artista: any) => artista.nome || artista) : [],
      designers: Array.isArray(jogo.designers) ? jogo.designers.map((designer: any) => designer.nome || designer) : [],
      bgg_id: jogo.id_bgg || null,
      complexity: jogo.complexidade || null,
      stats: {
        owned_count: jogo.qt_tem || 0,
        owned_before_count: jogo.qt_teve || 0,
        favorite_count: jogo.qt_favorito || 0,
        wishlist_count: jogo.qt_quer || 0,
        played_count: jogo.qt_jogou || 0
      }
    }));
    
    return {
      games,
      total: data.colecao.total || games.length,
      page,
      per_page: perPage
    };
  } catch (error) {
    console.error('[Ludopedia] Erro ao buscar coleção:', error);
    return null;
  }
}

/**
 * Importa a coleção de jogos do usuário da Ludopedia
 * @param userId ID do usuário no sistema
 * @returns Resultado da importação
 */
export async function importLudopediaCollection(userId: string) {
  try {
    console.log('[Ludopedia] Iniciando importação da coleção para o usuário:', userId);
    
    const token = await getUserToken(userId);
    
    if (!token) {
      return {
        success: false,
        error: 'Token de acesso da Ludopedia não encontrado'
      };
    }
    
    // Obter a primeira página para saber o total de jogos
    const firstPage = await getLudopediaCollection(token, 1, 50);
    
    if (!firstPage) {
      return {
        success: false,
        error: 'Não foi possível obter a coleção da Ludopedia'
      };
    }
    
    console.log(`[Ludopedia] Encontrados ${firstPage.total} jogos na coleção`);
    
    // Calcular o total de páginas
    const totalPages = Math.ceil(firstPage.total / firstPage.per_page);
    console.log(`[Ludopedia] Total de ${totalPages} páginas a importar`);
    
    // Inicializar contadores
    let importedCount = 0;
    let errorCount = 0;
    
    // Importar a primeira página que já foi buscada
    const firstPageResult = await importLudopediaGames(userId, firstPage.games);
    importedCount += firstPageResult.imported;
    errorCount += firstPageResult.errors;
    
    // Importar as demais páginas, se houver
    if (totalPages > 1) {
      for (let page = 2; page <= totalPages; page++) {
        console.log(`[Ludopedia] Importando página ${page} de ${totalPages}`);
        
        // Obter a página atual
        const currentPage = await getLudopediaCollection(token, page, 50);
        
        if (currentPage && currentPage.games.length > 0) {
          // Importar os jogos da página atual
          const pageResult = await importLudopediaGames(userId, currentPage.games);
          importedCount += pageResult.imported;
          errorCount += pageResult.errors;
        } else {
          console.error(`[Ludopedia] Erro ao obter página ${page}`);
          errorCount++;
        }
      }
    }
    
    console.log('[Ludopedia] Importação concluída');
    return {
      success: true,
      results: {
        total: firstPage.total,
        imported: importedCount,
        errors: errorCount
      }
    };
  } catch (error) {
    console.error('[Ludopedia] Erro ao importar coleção:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Erro desconhecido'
    };
  }
}

/**
 * Importa uma lista de jogos da Ludopedia para o banco de dados
 * @param userId ID do usuário no sistema
 * @param games Lista de jogos da Ludopedia
 * @returns Resultado da importação
 */
async function importLudopediaGames(userId: string, games: LudopediaGame[]) {
  let imported = 0;
  let errors = 0;
  
  try {
    // Importar os jogos em lote
    console.log(`[Ludopedia] Importando ${games.length} jogos`);
    
    for (const game of games) {
      try {
        // Procurar o jogo pelo ID da Ludopedia
        const { data: existingGame, error: searchError } = await supabase
          .from('games')
          .select('id')
          .eq('ludopedia_id', game.id)
          .single();
        
        let gameId: string;
        
        // Se o jogo já existe, usar o ID existente
        if (existingGame) {
          gameId = existingGame.id;
          console.log(`[Ludopedia] Jogo já existe: ${game.name} (${gameId})`);
        } else {
          // Se o jogo não existe, inseri-lo no banco
          console.log(`[Ludopedia] Inserindo novo jogo: ${game.name}`);
          
          const { data: newGame, error: insertError } = await supabase
            .from('games')
            .insert({
              name: game.name,
              year: game.year || null,
              ano_nacional: game.ano_nacional || null,
              description: null, // Não temos descrição da Ludopedia
              image_url: game.image_url || null,
              thumb_url: game.thumb_url || null,
              categories: game.categories || [],
              mechanics: game.mechanics || [],
              themes: game.themes || [],
              artists: game.artists || [],
              designers: game.designers || [],
              min_players: game.min_players || null,
              max_players: game.max_players || null,
              play_time: game.max_playing_time || game.min_playing_time || null,
              min_age: game.min_age || null,
              complexity: game.complexity || null,
              bgg_id: game.bgg_id || null,
              ludopedia_id: game.id
            })
            .select('id')
            .single();
          
          if (insertError || !newGame) {
            console.error(`[Ludopedia] Erro ao inserir jogo ${game.name}:`, insertError);
            errors++;
            continue;
          }
          
          gameId = newGame.id;
          console.log(`[Ludopedia] Jogo inserido: ${game.name} (${gameId})`);
        }
        
        // Criar ou atualizar a relação usuário-jogo
        const { error: userGameError } = await supabase
          .from('user_games')
          .upsert({
            user_id: userId,
            game_id: gameId,
            owned: true, // Se está na coleção, é porque possui
            wishlist: false,
            play_count: 0,
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'user_id,game_id'
          });
        
        if (userGameError) {
          console.error(`[Ludopedia] Erro ao atualizar relação usuário-jogo para ${game.name}:`, userGameError);
          errors++;
          continue;
        }
        
        imported++;
      } catch (gameError) {
        console.error(`[Ludopedia] Erro ao processar jogo ${game.name}:`, gameError);
        errors++;
      }
    }
    
    return { imported, errors };
  } catch (error) {
    console.error('[Ludopedia] Erro ao importar jogos:', error);
    return { imported, errors: errors + games.length - imported };
  }
} 